import { UserType } from '@metadevprofile/shared';
import { Spinner } from '@metadevprofile/ui/web';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { ImpersonationBanner } from '@/admin-user/components/impersonation-banner';
import { authRoutes as authRoutePaths } from '@/auth/constants/routes';
import { selectUser } from '@/auth/stores/auth-slice';
import { PUBLIC_ROUTES } from '@/common/constants/routes';
import { useStoreHydration } from '@/common/hooks/use-store-hydration';
import { getDashboardRoute, getRouteByUserType } from '@/common/lib/get-route-by-user-type';
import { useAppSelector } from '@/common/redux/store';
import { useInstitution } from '@/institution/hooks/use-institution';
import { onboardingRoutes } from '@/onboarding/constants/routes';
import { useProfile } from '@/profile/hooks/use-profile';

function InstitutionBlockedScreen() {
  const { t } = useTranslation('institution');

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center max-w-md space-y-4 p-8">
        <AlertTriangle className="h-12 w-12 text-danger mx-auto" />
        <h2 className="text-xl font-semibold">{t('billing.blocked.title')}</h2>
        <p className="text-muted">{t('billing.blocked.description')}</p>
      </div>
    </div>
  );
}

export function AuthGuard() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector(selectUser);
  const { profile, loading: profileLoading, isError: profileError, isConfirmedAbsent } = useProfile({ skip: !user });
  const shouldCheckInstitution = user?.type === UserType.PRACTITIONER && !!user.institution_id;
  const { institution, loading: institutionLoading } = useInstitution({ skip: !shouldCheckInstitution });
  const isHydrated = useStoreHydration();
  const [isBlocked, setIsBlocked] = useState(false);

  const isPublicPage = PUBLIC_ROUTES.some((route) => pathname.includes(route));

  useEffect(() => {
    if (!isHydrated || !user || !profile || !shouldCheckInstitution || institutionLoading) return;

    if (institution && institution.subscription_status !== 'active' && institution.subscription_status !== 'trialing') {
      setIsBlocked(true);
    } else {
      setIsBlocked(false);
    }
  }, [isHydrated, user, profile, shouldCheckInstitution, institution, institutionLoading]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    if (!user) {
      if (!isPublicPage) navigate(authRoutePaths.signIn);
      return;
    }

    // Unlike the old persisted `profile-store`, `profile` now comes from an RTK Query fetch that
    // has to actually resolve before we know whether the user has completed onboarding — wait for
    // it rather than treating "not loaded yet" the same as "no profile". A transient error
    // (network/500) is NOT a confirmed absence either: without this, a fetch hiccup on an existing
    // user would send them to onboarding. Only stop waiting once the profile is loaded or its
    // absence is confirmed (404) — RTK Query keeps retrying in the background otherwise
    // (refetchOnFocus/refetchOnReconnect are on), and this effect re-runs when that resolves.
    if (profileLoading || (profileError && !isConfirmedAbsent)) return;

    if (!profile && isConfirmedAbsent && !pathname.includes(onboardingRoutes.createProfile)) {
      navigate(onboardingRoutes.createProfile);
      return;
    }

    if (profile) {
      if (isPublicPage) {
        const dashboardRoute = getDashboardRoute(user);
        if (pathname !== dashboardRoute) {
          navigate(dashboardRoute);
        }
        return;
      }

      const userRoutePrefix = getRouteByUserType(user);
      const isOnValidAuthenticatedRoute = pathname.startsWith(userRoutePrefix);

      if (!isOnValidAuthenticatedRoute && !isPublicPage) {
        navigate(getDashboardRoute(user));
      }
    }
  }, [user, profile, profileLoading, profileError, isConfirmedAbsent, pathname, navigate, isPublicPage, isHydrated]);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isBlocked) {
    return <InstitutionBlockedScreen />;
  }

  // Outlet renders even while `profile`/`institution` are still loading (no extra gate here,
  // same as institutionLoading above) — every downstream consumer already null-guards on them
  // (Footer, PractitionerProfileClient, InstitutionProfileClient, *UserProfileForm all render a
  // skeleton/loading state until their own `useProfile`/`useInstitution` resolves).
  return (
    <>
      <ImpersonationBanner />
      <Outlet />
    </>
  );
}
